import { InputValueType, NovelActSchema, NovelActTree, NovelChapterSchema, NovelSceneSchema, PointOfViewType } from '#/index';
import { ExecuteParams, PromptFunction, TextContainer, ListContainer } from '../../types';
import { parseFullContentToXML, parseFullSummaryToXML, getNovelActTree, getCodexEntryFromCustomInput, codexEntriesToXml, getTreeDataById, parseOutlineToXML } from '../../utils';
import { convertJSONContentToFormattedText } from '@/utils';
import { createCustomFunction } from './createFunction';

// 处理 fullNovel 值的通用函数
const processFullNovelValue = (val: string, novelActsTree: NovelActTree[]): NovelActTree[] => {
  // 检查是否是完整内容（fullContent 或 fullSummary）
  if (val.includes('fullContent') || val.includes('fullSummary')) {
    return novelActsTree;
  }

  // 使用单个正则表达式匹配所有模式
  const match = val.match(/^(content|summary):([a-zA-Z0-9]+)(?::([a-zA-Z0-9_-]+))?$/);
  if (match) {
    const [, , type, characterId] = match;
    return getNovelActTree(novelActsTree, {
      povs: [{
        type: 'pointOfView',
        value: type as PointOfViewType,
        characterId
      }]
    });
  }

  return [];
};

/**
 * 组合相关函数
 * @returns 组合相关函数对象
 */
const compositionFunctions: Record<string, PromptFunction> = {
  input: createCustomFunction(
    'input',
    '按Input名称获取自定义输入值',
    (context, inputName: ExecuteParams): ListContainer<any> | boolean | void => {
      const { novelActsTree, snippets } = context.globalContext.generateStore.getState();
      const inputDetail = Object.values(context.globalContext.currentPosition?.prompt.inputs || {})
        .find(input => input.name === inputName)
      const inputs = inputDetail ? context.globalContext.currentPosition?.inputs?.[inputDetail.id] : undefined
      const valuesMap: Partial<Record<InputValueType, string[]>> = {}
      if (inputs) {
        inputs.forEach(input => {
          if (valuesMap[input.type]) {
            valuesMap[input.type]!.push(input.value)
          } else {
            valuesMap[input.type] = [input.value]
          }
        })
      }

      if (inputDetail?.types.includes('checkbox')) {
        return valuesMap.checkbox?.[0] === '1'
      }

      const result: ExecuteParams[] = []

      const getOutlineTree = (type: 'content' | 'summary', actTrees?: NovelActTree[]) => {
        if (actTrees?.length) {
          result.push({
            type: 'outlineTree',
            data: actTrees,
            mode: type,
            rootName: type === 'content' ? 'novel' : 'outline',
          })
        }
      }

      inputDetail?.types.forEach(type => {
        switch (type) {
          case 'text':
            valuesMap.text?.forEach(val => {
              result.push({
                type: 'textContainer',
                title: val,
                defaultFormat: 'plain',
                formatters: {
                  plain: (container) => container.title,
                },
              })
            })
            break;
          case 'dropdown':
            const labels = inputDetail?.settings?.dropdown?.options?.filter(opt => valuesMap.dropdown?.includes(opt.id))
            if (labels?.length) {
              result.push({
                type: 'textContainer',
                title: labels.map(opt => opt.label).join(','),
                defaultFormat: 'plain',
                formatters: {
                  plain: (container) => container.title,
                },
              })
            }
            break;
          case 'fullNovel':
            // fullContent fullSummary content:thirdPerson content:thirdPerson:34MLV8KLsaedE5FJF4iCFW5g4Hb
            if (inputDetail?.settings?.fullNovel?.includes('content')) {
              valuesMap.fullNovel?.forEach(val => {
                if (val === 'fullContent' || val.includes('content:')) {
                  getOutlineTree('content', processFullNovelValue(val, novelActsTree))
                }
              })
            }
            if (inputDetail?.settings?.fullNovel?.includes('summary')) {
              valuesMap.fullNovel?.forEach(val => {
                if (val === 'fullSummary' || val.includes('summary:')) {
                  getOutlineTree('summary', processFullNovelValue(val, novelActsTree))
                }
              })
            }
            break;
          case 'act':
            valuesMap.acts?.forEach(val => {
              const act = getTreeDataById(context, val)
              const mode = inputDetail?.settings?.act?.mode || 'content'
              if (act) {
                result.push({
                  type: 'textContainer',
                  reference: act.data,
                  defaultFormat: 'xml',
                  formatters: {
                    xml: (container, ctx) => {
                      const act = container.reference as NovelActSchema
                      return parseOutlineToXML(
                        getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { actIds: [act.id] }
                        ), ctx, 'novel', mode);
                    },
                  },
                })
              }
            })
            break;
          case 'chapter':
            valuesMap.chapters?.forEach(val => {
              const chapter = getTreeDataById(context, val)
              const mode = inputDetail?.settings?.chapter?.mode || 'content'
              if (chapter) {
                result.push({
                  type: 'textContainer',
                  reference: chapter.data,
                  defaultFormat: 'xml',
                  formatters: {
                    xml: (container, ctx) => {
                      const chapter = container.reference as NovelChapterSchema
                      return parseOutlineToXML(
                        getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { chapterIds: [chapter.id] }
                        ), ctx, 'novel', mode);
                    },
                  },
                })
              }
            })
            break;
          case 'scene':
            valuesMap.scenes?.forEach(val => {
              const scene = getTreeDataById(context, val)
              const mode = inputDetail?.settings?.scene?.mode || 'content'
              if (scene) {
                result.push({
                  type: 'textContainer',
                  reference: scene.data,
                  defaultFormat: 'xml',
                  formatters: {
                    xml: (container, ctx) => {
                      const scene = container.reference as NovelSceneSchema
                      return parseOutlineToXML(
                        getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { sceneIds: [scene.id] }),
                        ctx, 'novel', mode);
                    },
                  },
                })
              }
            })
            break;
          case 'codex':
            if (inputs?.length) {
              getCodexEntryFromCustomInput(inputs, context.globalContext.generateStore).forEach(entry => {
                result.push(entry)
              })
            }
            break;
          case 'label':
            valuesMap.labels?.forEach(val => {
              const labelDetail = context.globalContext.currentPosition?.novel?.attributes.labels.find(label => label.id === val)
              if (labelDetail) {
                result.push({
                  type: 'novelLabel',
                  data: labelDetail,
                })
              }
            })
            break;
          case 'snippet':
            valuesMap.snippets?.forEach(val => {
              const snippet = snippets.find(snippet => snippet.attributes.title === val)
              if (snippet) {
                result.push(snippet)
              }
            })
            break;
        }
      })
      if (!result.length) return;
      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      };
    },
    [{ name: 'inputName', type: 'string', required: true }],
  ),
  include: createCustomFunction(
    'include',
    '包含外部提示词（支持递归渲染）',
    (context, path) => {
      // 这里需要访问 PromptRenderer 的 renderInclude 方法
      // 由于函数执行器无法直接访问渲染器，我们需要通过上下文传递
      if (context.renderInclude && typeof path === 'string') {
        return context.renderInclude(path, context);
      }
      return '';
    },
    [{ name: 'path', type: 'string', required: true }],
  ),
  // 本地存储函数
  local: createCustomFunction(
    'local',
    '在本地内存中存储或检索值（区分大小写）。如果提供值则存储，否则返回存储的值',
    (context, key, value): ExecuteParams => {
      if (typeof key !== 'string') return undefined;
      // 初始化本地内存
      if (!context.localMemory) {
        context.localMemory = new Map<string, ExecuteParams>();
      }

      // 如果提供了值，则存储
      if (value !== undefined) {
        context.localMemory.set(key, value);
        return value;
      } else {
        // 否则返回存储的值，如果不存在则返回空字符串
        return context.localMemory.get(key) || undefined;
      }
    },
    [{ name: 'key', type: 'string', required: true }, { name: 'value', type: 'string', required: false }],
  ),
}

export default compositionFunctions;